import Moment from 'moment'
import firebase from 'firebase/app'
import {db} from '@/plugins/firestore'
import sw from '@/utils/sweetAlert'
import config from '@/utils/const'

export default {
  /**
   * サインインユーザーのデータをfirebaseから取得
   * @param {Object} state store.state参照
   * @param {Object} commit mutations参照
   * @returns {Promise}
   */
  async initializeUser ({state, commit}) {
    const mail = firebase.auth().currentUser.email
    await db.collection('tenants/').where('domains', 'array-contains', mail.split('@')[1]).get()
      .then((tenant) => {
        if (tenant.empty || tenant.docs[0].data().status === config.tenantStatus.INVALID) {
          return Promise.reject('アクセス権限がありません')
        }
        if (tenant.docs.length >= 2) {
          return Promise.reject('テナント情報が重複しています<br>管理者に問い合わせてください')
        }
        if (tenant.docs[0].data().status !== config.tenantStatus.VALIDITY) {
          return Promise.reject('導入が完了していないテナントです')
        }
        commit('setTenant', tenant.docs[0].id)
      })
      .catch((error) => {
        sw.infoAlert(error)
        commit('setIsLoading', false)
        commit('setIsUsersError', true)
        console.error('エラー:' + error)
        return Promise.reject(error)
      })
    return db.collection('tenants/' + state.tenant + '/users').where('mail', '==', mail).get()
      .then((user) => {
        if (user.empty) {
          sw.infoAlert('登録されていません<br>管理者に問い合わせてください')
          return
        }
        if (user.docs.length >= 2) {
          sw.infoAlert('ユーザー情報が重複しています<br>管理者に問い合わせてください')
          return
        }
        if (!user.docs[0].data().isActive) {
          sw.infoAlert('有効化されていません<br>管理者に問い合わせてください')
          return
        }
        commit('setCurrentUser', {
          id: user.docs[0].id,
          mail: user.docs[0].data().mail,
          priority: user.docs[0].data().priority,
          name: {
            last: user.docs[0].data().name.last,
            first: user.docs[0].data().name.first
          },
          isAdmin: user.docs[0].data().isAdmin
        })
      })
      .catch((error) => {
        sw.infoAlert('ログインに失敗しました')
        commit('setIsLoading', false)
        commit('setIsUsersError', true)
        console.error(`エラー:${error}`)
        return Promise.reject(error)
      })
  },
  /**
   * グループのデータをfirebaseから取得
   * @param {Object} state store.state参照
   * @param {Object} commit mutations参照
   * @returns {Promise}
   */
  async getGroups ({state, commit}) {
    let isRelations = false
    await db.doc('tenants/' + state.tenant).get()
      .then((doc) => {
        isRelations = doc.data().isRelations
      })
      .catch((error) => {
        commit('setIsLoading', false)
        commit('setExistsGroup', false)
        commit('setExistsRelation', false)
        commit('setIsGroupsError', true)
        console.error(`エラー:${error}`)
        return Promise.reject(error)
      })
    if (!isRelations) return
    return db.collection('tenants/' + state.tenant + '/relations').doc('root').get()
      .then(async (relation) => {
        if (relation.empty) return
        let groups = []
        await Promise.all(relation.data().children.groups.map(group => {
          return db.doc('tenants/' + state.tenant + '/groups/' + group.id).get()
            .then((doc) => {
              if (!doc.data() || !doc.data().isDisplay) return
              groups.push({
                id: group.id,
                name: doc.data().name,
                children: {
                  groups :[],
                  users: []
                },
                isLoaded: false,
                priority: group.priority
              })
            })
        }))
        commit('addGroups', groups)
        commit('setExistsGroup', !relation.empty)
        commit('setExistsRelation', (!relation.empty && groups.length))
        return
      })
      .catch((error) => {
        commit('setIsLoading', false)
        commit('setExistsGroup', false)
        commit('setExistsRelation', false)
        commit('setIsGroupsError', true)
        console.error(`エラー:${error}`)
        return Promise.reject(error)
      })
  },
  /**
   * 選択されたグループの子を取得
   * @param {Object} state store.state参照
   * @param {Object} commit mutations参照
   * @param {Object} group 選択されているグループ
   * @returns {Promise}
   */
  getChildrenUsers ({state, commit}, group) {
    return db.collection('tenants/' + state.tenant + '/relations').where('currentId', '==', group.id).get()
      .then((query) => {
        query.forEach((doc) => {
          let childrenUsers = []
          doc.data().children.users.forEach((g) => {
            childrenUsers.push({
              id: g.id,
              priority: g.priority
            })
          })
          commit('setChildrenUsers', childrenUsers)
        })
      })
      .catch((error) => {
        commit('setIsLoading', false)
        commit('setIsGroupsError', true)
        console.error(`エラー:${error}`)
        return Promise.reject(error)
      })
  },
  /**
   * 全ユーザーのデータをfirebaseから取得
   * @param {Object} state store.state参照
   * @param {Object} commit mutations参照
   * @returns {Promise}
   */
  async getUsers ({state, commit}) {
    try {
      commit('setIsLoading', true)
      commit('setIsUsersError', false)
      let users = []
      if (!state.existsGroup) {
        let selectedUsers = []
        await db.collection('tenants/' + state.tenant + '/users').orderBy('priority').get().then((query) => {
          query.forEach((user) => {
            users.push({
              id: user.id,
              mail: user.data().mail,
              priority: user.data().priority,
              name: {
                last: user.data().name.last,
                first: user.data().name.first
              },
              isDisplay: user.data().isDisplay,
              isActive: user.data().isActive
            })
            selectedUsers.push({
              id: user.id,
              priority: user.data().priority
            })
          })
        })
        commit('setSelectedUsers', selectedUsers)
      } else {
        await Promise.all(state.selectedUsers.map(async selectUser => {
          if (state.users.data.find(u => u.id === selectUser.id)) return
          return db.doc('tenants/' + state.tenant + '/users/' + selectUser.id).get().then((user) => {
            if (!user.exists) return
            users.push({
              id: user.id,
              mail: user.data().mail,
              priority: selectUser.priority,
              name: {
                last: user.data().name.last,
                first: user.data().name.first
              },
              isDisplay: user.data().isDisplay,
              isActive: user.data().isActive
            })
          })
        }))
      }
      if (users.length) commit('addUsers', users)
    } catch (error) {
      commit('setIsLoading', false)
      commit('setIsUsersError', true)
      console.error(`エラー:${error}`)
      return Promise.reject(error)
    }
  },
  /**
   * ユーザーデータと表示日付を元に日報を取得
   * @param {Object} state store.state参照
   * @param {Object} commit mutations参照
   * @param {Object} dispatch actions参照
   * @returns {Promise}
   */
  async getDiaries ({state, commit, dispatch}) {
    try {
      commit('setIsLoading', true)
      commit('setIsDiariesError', false)
      await Promise.all(state.selectedUsers.map(async user => {
        let diaries = {}
        let targetUser = state.users.data.find(u => u.id === user.id)
        if (targetUser === undefined) return
        // 読み込み中のユーザーがサインインユーザーなら下書きを読み込む
        if (state.currentUser.mail === targetUser.mail) {
          diaries = await dispatch('getDrafts', targetUser.id)
        }
        // user.idと表示月の初日、最終日を用いユーザーの表示月の日報を取得する
        return db.collection('tenants/' + state.tenant + '/users/' + targetUser.id + '/diary')
        .where('date', '>=', Moment(state.thisDate).startOf('month').format('YYYY-MM-DD'))
        .where('date', '<=', Moment(state.thisDate).endOf('month').format('YYYY-MM-DD')).get().then((query) => {
          query.forEach((doc) => {
            const startHour = Number(doc.data().timeStart.split(':')[0])
            const startMinutes = Number(doc.data().timeStart.split(':')[1])
            const endHour = Number(doc.data().timeEnd.split(':')[0])
            const endMinutes = Number(doc.data().timeEnd.split(':')[1])
            const breakHour = Number(doc.data().timeBreak.split(':')[0])
            const breakMinutes = Number(doc.data().timeBreak.split(':')[1])
            const timeStart = startHour + startMinutes / 60
            const timeEnd = endHour + endMinutes / 60
            const timeBreak = breakHour + breakMinutes / 60
            let displayTimeMinutes = startMinutes > endMinutes
              ? (60 - startMinutes) + endMinutes
              : endMinutes - startMinutes
            displayTimeMinutes = breakMinutes !== 0 || breakMinutes > displayTimeMinutes
              ? 60 - (breakMinutes - displayTimeMinutes)
              : displayTimeMinutes
            let displayTimeHour = endHour - startHour - breakHour
            if (breakMinutes > startMinutes || startMinutes > endMinutes) {
              displayTimeHour -=1
            }
            diaries[doc.data().date] = {
              date: doc.data().date,
              time: Math.round((timeEnd - timeStart - timeBreak) * 100) / 100,
              displayTime: displayTimeHour === 0 && displayTimeMinutes === 0
                        ? '0h'
                        : (displayTimeHour === 0 ? '' : displayTimeHour + 'h') + (displayTimeMinutes === 0 ? '' : displayTimeMinutes + 'm'),
              feel: config.faceIcon[doc.data().feel],
              actual: doc.data().actual,
              summary: doc.data().summary,
              note: doc.data().note,
              plan: doc.data().plan,
              event: doc.data().event
            }
          })
          commit('setDiaries', {id: targetUser.id, diaries: diaries})
        })
      }))
    } catch(error) {
      commit('setIsDiariesError', true)
      console.error(`エラー:${error}`)
      return Promise.reject(error)
    } finally {
      commit('setIsLoading', false)
    }
  },
  /**
   * サインインユーザーの下書きを取得
   * @param {Object} state store.state参照
   * @param {String} userId 日報を取得するユーザーのID
   * @returns {object} サインインユーザーの下書き
   */
  async getDrafts ({state}, userId) {
    let drafts = {}
    await db.collection('tenants/' + state.tenant + '/users/' + userId + '/draft')
      .where('date', '>=', Moment(state.thisDate).startOf('month').format('YYYY-MM-DD'))
      .where('date', '<=', Moment(state.thisDate).endOf('month').format('YYYY-MM-DD')).get().then((query) => {
        query.forEach((doc) => {
          const startHour = Number(doc.data().timeStart.split(':')[0])
          const startMinutes = Number(doc.data().timeStart.split(':')[1])
          const endHour = Number(doc.data().timeEnd.split(':')[0])
          const endMinutes = Number(doc.data().timeEnd.split(':')[1])
          const breakHour = Number(doc.data().timeBreak.split(':')[0])
          const breakMinutes = Number(doc.data().timeBreak.split(':')[1])
          const timeStart = startHour + startMinutes / 60
          const timeEnd = endHour + endMinutes / 60
          const timeBreak = breakHour + breakMinutes / 60
          let displayTimeMinutes = startMinutes > endMinutes
            ? (60 - startMinutes) + endMinutes
            : endMinutes - startMinutes
          displayTimeMinutes = breakMinutes !== 0 || breakMinutes > displayTimeMinutes
            ? 60 - (breakMinutes - displayTimeMinutes)
            : displayTimeMinutes
          let displayTimeHour = endHour - startHour - breakHour
          if (breakMinutes > startMinutes || startMinutes > endMinutes) {
            displayTimeHour -=1
          }
          drafts[doc.data().date] = {
            date: doc.data().date,
            time: Math.round((timeEnd - timeStart - timeBreak) * 100) / 100,
            displayTime: displayTimeHour !== 0 || displayTimeMinutes !== 0
                      ? (displayTimeHour === 0 ? '' : displayTimeHour + 'h') + (displayTimeMinutes === 0 ? '' : displayTimeMinutes + 'm')
                      : '0h',
            feel: 'edit',
            actual: doc.data().actual,
            summary: doc.data().summary,
            note: doc.data().note,
            plan: doc.data().plan,
            event: doc.data().event
          }
        })
      })
    return drafts
  }
}



// WEBPACK FOOTER //
// ./src/store/actions.js